import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ProductUid } from '@constants';
import { BreakpointService, ProductQueryParamService, ProductService } from '@services';
import { Observable, of, Subscription, switchMap, take, tap } from 'rxjs';
import { SelectComponent } from '../../@shared/components/select/select.component';
import { AppHeaderProductSelectService } from '../app-header-product-select.service';

@Component({
    selector: 'app-header-product-select',
    imports: [SelectComponent],
    templateUrl: './app-header-product-select.component.html',
    styleUrls: ['./app-header-product-select.component.scss'],
})
export class AppHeaderProductSelectComponent implements OnInit, OnChanges, OnDestroy {
    private cdr = inject(ChangeDetectorRef);
    private breakpoint = inject(BreakpointService);
    private productService = inject(ProductService);
    private productQueryParamService = inject(ProductQueryParamService);
    private appHeaderProductSelectService = inject(AppHeaderProductSelectService);

    /** rxjs subscriptions that should get cleaned up when the component is destroyed */
    private subs: Subscription[] = [];
    /** form control for the select */
    control = new FormControl<ProductUid>(ProductUid.GENESYS_CLOUD_CX);
    /** select options */
    options: { text: string; value: string }[];
    /** specific a custom tab index for accessibility */
    @Input() tabIndex: number;
    /** Color input with default value */
    @Input() color: string = 'white';

    @Output() productSelected = new EventEmitter<void>();
    isMobile$: Observable<boolean>;

    ngOnInit(): void {
        this.isMobile$ = this.breakpoint.mobile$;
        this.options = this.productService.availableProducts.map((p) => ({ text: p.displayName, value: p.uid }));

        this.subs.push(
            this.productService.selectedProduct$.subscribe((data) => {
                // NOTE: We have to do this to make the ng-select binding to work
                // cannot do the | async binding to the ngModel
                this.control.setValue(data.uid);
            }),
        );

        this.subs.push(
            this.control.valueChanges
                .pipe(
                    switchMap((selectedUid) => {
                        if (!selectedUid) return of(null);
                        this.productQueryParamService.setProductQueryParam(selectedUid);
                        this.appHeaderProductSelectService.handleProductNavigation(selectedUid);
                        return this.isMobile$.pipe(
                            take(1),
                            tap((isMobile) => {
                                if (isMobile) {
                                    this.productSelected.emit();
                                }
                            }),
                        );
                    }),
                )
                .subscribe(),
        );
    }

    /**
     * Handle Product changes
     * @param changes - Simple Changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tabIndex']) {
            this.tabIndex = changes['tabIndex'].currentValue;
        }

        this.cdr.detectChanges();
    }

    /**
     * Unsubscribe from all subscriptions on destroy
     * @returns void
     */
    ngOnDestroy(): void {
        this.subs?.forEach((sub) => sub?.unsubscribe());
    }
}
